/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ChangeEvent, ReactNode } from 'react';
import {
  InputProps,
  renderWrapper,
  Checkbox,
  CheckboxProps,
  TextArea,
  TextAreaProps,
  RadioButtonGroupProps,
  RadioButtonGroup
} from 'components/Shared/Form';
import { Input } from 'components/Shared/Form/themes/BGH/input';
import InputSelect, { InputSelectProps } from './InputSelect/';
import { LabeledInput } from './LabeledInput/themes/BGH';
import { ToolTip } from '../ToolTip';
import { DropDownSelect, SelectProps } from './Select/select';

type RenderRadioGroupProps = Partial<Omit<RadioButtonGroupProps, 'onChange'>>;
type RenderInputProps = Partial<Omit<InputProps, 'onChange'>>;
type RenderTextAreaProps = Partial<Omit<TextAreaProps, 'onChange'>>;

export const RenderRadioGroup = renderWrapper<RenderRadioGroupProps>(
  (input, props, { error, active, touched, invalid, valid }) => {
    return (
      <RadioButtonGroup
        {...{
          ...input,
          ...props,
          id: input.name,
          checked: props.checked ? true : false,
          invalid: !active && error && touched && invalid,
          valid: !!input.value && valid,
          items: props.items
        }}
      />
    );
  }
);

export const RenderInput = renderWrapper<RenderInputProps>(
  (input, props, { error, active, touched, invalid, valid }) => {
    return (
      <Input
        {...{
          ...input,
          ...props,
          id: input.name,
          invalid: !active && error && touched && invalid,
          valid: !!input.value && valid
        }}
      />
    );
  }
);

interface RenderLabeledInputProps extends RenderInputProps {
  customLabel?: string;
  inputClassName?: string;
  customValid?: boolean;
}
export const RenderLabeledInput = renderWrapper<RenderLabeledInputProps>(
  (
    input,
    { customLabel, inputClassName, customValid, ...props },
    { error, active, touched, invalid, valid }
  ) => {
    const isValid = typeof customValid === 'boolean' ? customValid : !!input.value && valid;

    return (
      <LabeledInput
        {...{
          ...input,
          ...props,
          id: input.name,
          invalid: !active && error && touched && invalid,
          valid: isValid,
          label: customLabel,
          className: inputClassName
        }}
      />
    );
  }
);

export const RenderTextArea = renderWrapper<RenderTextAreaProps>(
  (input, props, { error, active, touched, invalid, valid }) => {
    return (
      <TextArea
        {...{
          ...input,
          ...props,
          id: input.name,
          invalid: !active && error && touched && invalid,
          valid: !!input.value && valid
        }}
      />
    );
  }
);

export const RenderFileInput = renderWrapper<RenderInputProps>(
  // separate out value from rest of input, value should be controlled by the input itself
  ({ value, ...input }, props, { error, active, touched, invalid }) => {
    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      input.onChange(e.target.files);
    };

    return (
      <Input
        {...{
          ...input,
          ...props,
          id: input.name,
          invalid: !active && error && touched && invalid,
          onChange
        }}
      />
    );
  }
);

interface RenderCheckbox extends Partial<Omit<CheckboxProps, 'onChange'>> {
  customLabel?: string;
  checkboxClassName?: string;
  tooltipText?: ReactNode;
}
export const RenderCheckbox = renderWrapper<RenderCheckbox>(
  (
    input,
    { customLabel, checkboxClassName, tooltipText, ...props },
    { error, active, touched, invalid }
  ) => {
    const onChange = input.onChange as () => void;
    return (
      <>
        <Checkbox
          {...{
            ...input,
            ...props,
            appearance: props.appearance || 'secondary',
            id: input.name,
            onChange,
            checked: input.value,
            className: checkboxClassName,
            invalid: !active && error && touched && invalid,
            label: customLabel
          }}
        />
        {tooltipText && <ToolTip text={tooltipText} />}
      </>
    );
  }
);

export interface RenderLabeledInputSelectProps extends InputSelectProps {
  customLabel?: string;
  inputClassName?: string;
  customValid?: boolean;
}
export const RenderLabeledInputSelect = renderWrapper<RenderLabeledInputSelectProps>(
  (
    input,
    { customLabel, inputClassName, customValid, ...props },
    { error, active, touched, invalid, valid }
  ) => {
    const isValid = typeof customValid === 'boolean' ? customValid : !!input.value && valid;

    return (
      <InputSelect
        useLabeledInput
        {...{
          ...input,
          ...props,
          invalid: !active && error && touched && invalid,
          valid: isValid,
          label: customLabel,
          inputClassName
        }}
      />
    );
  }
);

export const RenderInputSelect = renderWrapper<InputSelectProps>(
  (input, props, { error, active, touched, invalid, valid }) => (
    <InputSelect
      {...{
        ...input,
        ...props,
        invalid: !active && error && touched && invalid,
        valid: !!input.value && valid
      }}
    />
  )
);

export const RenderDropDownSelect = renderWrapper<SelectProps>(
  (input, props, { error, active, touched, invalid, valid }) => {
    return (
      <DropDownSelect
        {...{
          ...input,
          ...props,
          invalid: !active && error && touched && invalid,
          valid: !!input.value && valid
        }}
      />
    );
  }
);
